import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InsightCard } from './InsightCard';
import { useBehavioralInsights } from '@/hooks/useBehavioralInsights';
import type { GeneratedInsight } from '@/lib/insightNarrativeUtils';

interface BehavioralInsightsProps {
  year?: number;
}

export function BehavioralInsights({ year }: BehavioralInsightsProps) {
  const navigate = useNavigate();
  const { insights, isLoading, hasEnoughData } = useBehavioralInsights(year);

  const handleCtaClick = (insight: GeneratedInsight) => {
    if (!insight.cta) return;

    if (insight.cta.action === 'chat' && insight.cta.context) {
      // Navigate to Discover page with the context pre-filled
      navigate('/discover', { 
        state: { 
          initialMessage: insight.cta.context 
        } 
      });
    } else if (insight.cta.action === 'discover') {
      navigate('/discover');
    }
  };

  // Don't render if loading or no insights
  if (isLoading) {
    return (
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            How You Actually Read
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-32 bg-muted/40 rounded-lg" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasEnoughData || insights.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="card-elevated overflow-hidden">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            How You Actually Read
            <Sparkles className="w-4 h-4 text-star ml-1" />
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Patterns and insights from your reading behavior
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {insights.map((insight, index) => (
              <InsightCard
                key={insight.id}
                insight={insight}
                index={index}
                onCtaClick={handleCtaClick}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
